<template>
  <DataTable
      :value="engagements"
      :loading="loading"
      striped-rows
      size="small"
      scrollable
      scroll-height="flex"
      :pt="{
        table: { class: 'text-sm' },
        wrapper: { class: 'overflow-auto' },
        headerCell: { class: 'text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/50' },
        bodyCell: { class: 'text-gray-700 dark:text-gray-300' }
      }"
    >
      <template #empty>
        <div class="flex flex-col items-center justify-center py-12 text-center">
          <i class="pi pi-users mb-3 text-4xl text-gray-300 dark:text-gray-600" />
          <p class="text-sm text-gray-500 dark:text-gray-400">No engagement data found</p>
          <p class="mt-1 text-xs text-gray-400 dark:text-gray-500">Try adjusting the date range or search filters</p>
        </div>
      </template>

      <!-- Date/Time -->
      <Column
        field="timestamp"
        header="Date/Time"
        :style="{ minWidth: '140px' }"
        frozen
      >
        <template #body="{ data }">
          <span class="whitespace-nowrap text-xs">
            {{ formatDateTime(data.timestamp) }}
          </span>
        </template>
      </Column>

      <!-- Email -->
      <Column
        field="email"
        header="Email"
        :style="{ minWidth: '200px' }"
        frozen
      >
        <template #body="{ data }">
          <span class="font-medium">{{ getEmail(data) }}</span>
        </template>
      </Column>

      <!-- Dynamic Question Columns -->
      <Column
        v-for="col in questionColumns"
        :key="col.segmentGroupId"
        :header="col.name"
        :style="{ minWidth: '160px' }"
      >
        <template #body="{ data }">
          <span
            v-if="getAnswerForColumn(data, col)"
            v-tooltip.top="getAnswerForColumn(data, col).length > 60 ? getAnswerForColumn(data, col) : undefined"
            class="line-clamp-2 max-w-[160px]"
          >{{ getAnswerForColumn(data, col) }}</span>
          <span v-else>--</span>
        </template>
      </Column>

      <!-- Outcome -->
      <Column
        header="Outcome"
        :style="{ minWidth: '180px' }"
      >
        <template #body="{ data }">
          <span class="whitespace-nowrap">{{ getOutcome(data) }}</span>
        </template>
      </Column>

      <!-- Page URL -->
      <Column
        header="Page"
        :style="{ minWidth: '160px' }"
      >
        <template #body="{ data }">
          <span
            v-if="data.pageUrl"
            v-tooltip.top="data.pageUrl"
            class="max-w-[160px] truncate text-xs"
          >
            {{ truncateUrl(data.pageUrl) }}
          </span>
          <span v-else class="text-gray-400 dark:text-gray-500">--</span>
        </template>
      </Column>

      <!-- Status -->
      <Column
        header="Status"
        :style="{ minWidth: '110px' }"
      >
        <template #body="{ data }">
          <Tag
            :value="formatStatus(data.completionStatus)"
            :severity="getStatusSeverity(data.completionStatus)"
            class="text-xs"
          />
        </template>
      </Column>

      <!-- Duration -->
      <Column
        header="Duration"
        :style="{ minWidth: '90px' }"
      >
        <template #body="{ data }">
          <span class="whitespace-nowrap text-xs">
            {{ formatDuration(data.durationMs) }}
          </span>
        </template>
      </Column>

      <!-- Device -->
      <Column
        header="Device"
        :style="{ minWidth: '80px' }"
      >
        <template #body="{ data }">
          <span class="text-xs capitalize">{{ data.deviceType || '--' }}</span>
        </template>
      </Column>

      <!-- Country -->
      <Column
        header="Country"
        :style="{ minWidth: '80px' }"
      >
        <template #body="{ data }">
          <span class="text-xs">{{ data.country || '--' }}</span>
        </template>
      </Column>
    </DataTable>
</template>

<script setup lang="ts">
import type { FlowEngagement } from '../services/flowMetricsService'
import { segmentName } from '@/modules/segment/services/segmentUtilities'
import { stripHtml } from '@rightmessage/shared-utils'

interface QuestionColumn {
  segmentGroupId: string
  name: string
}

const props = defineProps<{
  engagements: FlowEngagement[]
  flowId: string
  loading: boolean
  totalRecords: number
}>()

defineEmits<{
  page: [event: { page: number }]
}>()

const configStore = useConfigStore()
const segmentGroupStore = useSegmentGroupStore()

// Identify input questions in this flow that collect email (for fallback email display)
const emailQuestionIds = computed<Set<string>>(() => {
  const ids = new Set<string>()
  const questions = configStore.config?.rightAsk?.questions
  if (!questions) return ids
  for (const q of questions as any[]) {
    if (q.displayType === 'input') {
      const text = (q.text || '').toLowerCase()
      if (text.includes('email')) {
        ids.add(q.id)
      }
    }
  }
  return ids
})

// Get email: prefer visitor.emailAddress, fall back to input_answer from email questions
function getEmail(engagement: FlowEngagement): string {
  if (engagement.email) return engagement.email
  // Look for input_answer nodes from email-type questions
  for (const node of engagement.nodes) {
    if (node.nodeType === 'input_answer' && node.questionId && emailQuestionIds.value.has(node.questionId)) {
      if (node.inputValue) return node.inputValue
    }
  }
  return ''
}

// Derive question columns from the flow's funnelDefinition
const questionColumns = computed<QuestionColumn[]>(() => {
  const flow = configStore.config?.rightCta?.offerFunnels?.find(
    (f: any) => f.id === props.flowId
  )
  if (!flow?.funnelDefinition) return []
  return extractQuestionColumns(flow.funnelDefinition)
})

function getQuestionLabel(segmentGroupId: string): string {
  // For dim_* IDs, use the dimension/segment group name
  const sg = segmentGroupStore.getById(segmentGroupId)
  if (sg?.name) return sg.name

  // For raq_* IDs (input questions), look up the question text from rightAsk
  const questions = configStore.config?.rightAsk?.questions
  if (questions) {
    const question = questions.find((q: any) => q.id === segmentGroupId)
    if (question?.text) return stripHtml(question.text)
  }

  return segmentGroupId
}

function extractQuestionColumns(funnelDefinition: any): QuestionColumn[] {
  const columns: QuestionColumn[] = []
  const seen = new Set<string>()

  function walk(nodes: any[]) {
    if (!nodes) return
    for (const node of nodes) {
      if (node.type === 'sg' && node.ask === true && node.segmentGroupId && !seen.has(node.segmentGroupId)) {
        seen.add(node.segmentGroupId)
        // Skip email input questions — their value is already shown in the built-in Email column
        if (emailQuestionIds.value.has(node.segmentGroupId)) continue
        columns.push({
          segmentGroupId: node.segmentGroupId,
          name: getQuestionLabel(node.segmentGroupId)
        })
      }
      if (node.child) walk(Array.isArray(node.child) ? node.child : [node.child])
      if (node.split) {
        for (const split of node.split) {
          if (split.child) walk(Array.isArray(split.child) ? split.child : [split.child])
        }
      }
      if (node.yes) walk(Array.isArray(node.yes) ? node.yes : [node.yes])
      if (node.no) walk(Array.isArray(node.no) ? node.no : [node.no])
    }
  }

  const root = funnelDefinition?.child || []
  walk(Array.isArray(root) ? root : [root])
  return columns
}

// Resolve answer for a given question column from engagement nodes
// Note: When "Other" is selected, flow_executions records TWO question_answer nodes
// for the same question — the first has freeformInput: null, the second has the actual text.
// We scan all matching nodes and prefer the one with freeformInput.
function getAnswerForColumn(engagement: FlowEngagement, col: QuestionColumn): string {
  // Collect all matching question_answer nodes for this column
  const matchingNodes = engagement.nodes.filter(
    (n) => n.nodeType === 'question_answer' && n.segmentGroupId === col.segmentGroupId
  )

  if (matchingNodes.length > 0) {
    // Prefer the node with freeformInput (the follow-up "Other" text entry)
    const nodeWithFreeform = matchingNodes.find((n) => n.freeformInput)
    const node = nodeWithFreeform || matchingNodes[0]

    if (node.selectedSegmentId && typeof node.selectedSegmentId === 'string') {
      const segment = segmentGroupStore.segmentForSegmentId(node.selectedSegmentId)
      if (segment) {
        const name = segmentName(segment) || segment.name || node.selectedSegmentId
        const result = stripHtml(name)
        if (node.freeformInput) {
          return `Other: ${stripHtml(node.freeformInput)}`
        }
        return result
      }
      if (node.freeformInput) {
        return `Other: ${stripHtml(node.freeformInput)}`
      }
      return node.selectedSegmentId
    }
    if (node.freeformInput) {
      return `Other: ${stripHtml(node.freeformInput)}`
    }
  }

  // Check for multi-select answers
  for (const node of engagement.nodes) {
    if (node.nodeType === 'question_answer_multiple' && node.segmentGroupId === col.segmentGroupId) {
      if (node.selectedSegmentId && Array.isArray(node.selectedSegmentId)) {
        const names = node.selectedSegmentId.map((segId: string) => {
          const segment = segmentGroupStore.segmentForSegmentId(segId)
          if (segment) {
            const name = segmentName(segment) || segment.name || segId
            return stripHtml(name)
          }
          return segId
        })
        let result = names.join(', ')
        if (node.otherText) {
          result += `, Other: ${stripHtml(node.otherText)}`
        }
        return result
      }
      if (node.otherText) {
        return `Other: ${stripHtml(node.otherText)}`
      }
    }

    if (node.nodeType === 'input_answer' && node.questionId === col.segmentGroupId) {
      return node.inputValue ? stripHtml(node.inputValue) : ''
    }
  }
  return ''
}

// Determine outcome from nodes (scan last to first)
function getOutcome(engagement: FlowEngagement): string {
  const nodes = [...engagement.nodes].reverse()

  for (const node of nodes) {
    if (node.nodeType === 'offer_conversion' && node.conversionType === 'submit') {
      return `Submitted: ${getOfferName(node.offerId)}`
    }
    if (node.nodeType === 'offer_conversion' && node.conversionType === 'click') {
      return `Clicked: ${getOfferName(node.offerId)}`
    }
    if (node.nodeType === 'offer') {
      return `Shown: ${getOfferName(node.offerId)}`
    }
    if (node.nodeType === 'redirect' || node.nodeType === 'redirect_conversion') {
      return `Redirected: ${truncateUrl(node.url || '')}`
    }
    if (node.nodeType === 'message') {
      return 'Shown message'
    }
    if (node.nodeType === 'exit') {
      return 'Exited'
    }
  }

  if (engagement.completionStatus === 'dismissed') return 'Dismissed'
  if (engagement.completionStatus === 'abandoned' || engagement.completionStatus === 'inactivity') return 'Abandoned'

  return engagement.completionStatus || '--'
}

function getOfferName(offerId?: string): string {
  if (!offerId) return 'Unknown'
  const offer = configStore.config?.rightCta?.offers?.find((o: any) => o.id === offerId)
  return offer?.name || offerId
}

// Formatting helpers
function formatDateTime(timestamp: string): string {
  const date = new Date(timestamp)
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  })
}

function formatDuration(ms: number): string {
  if (!ms || ms < 1000) return '<1s'
  const totalSeconds = Math.floor(ms / 1000)
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  if (minutes > 0) return `${minutes}m ${seconds}s`
  return `${seconds}s`
}

function formatStatus(status: string): string {
  if (!status) return 'Unknown'
  return status.charAt(0).toUpperCase() + status.slice(1)
}

function getStatusSeverity(status: string): string {
  switch (status) {
    case 'completed': return 'success'
    case 'abandoned':
    case 'inactivity': return 'warn'
    case 'dismissed': return 'danger'
    default: return 'secondary'
  }
}

function truncateUrl(url: string): string {
  if (!url) return ''
  try {
    const parsed = new URL(url)
    const path = parsed.pathname + parsed.search
    return path.length > 40 ? path.substring(0, 37) + '...' : path
  } catch {
    return url.length > 40 ? url.substring(0, 37) + '...' : url
  }
}
</script>
